const WishlistConfig = {
  cookieName: "sneakers_depot_wishlist",
  cookieDays: 365,
  maxItems: 200,
};

const WishlistStorage = {
  getItems() {
    const cookie = this.getCookie(WishlistConfig.cookieName);
    if (!cookie) return [];
    try {
      return JSON.parse(decodeURIComponent(cookie));
    } catch (e) {
      console.error("Wishlist: Error parsing cookie", e);
      return [];
    }
  },

  setItems(items) {
    const value = encodeURIComponent(JSON.stringify(items));
    const expires = new Date();
    expires.setTime(
      expires.getTime() + WishlistConfig.cookieDays * 24 * 60 * 60 * 1000
    );
    document.cookie = `${
      WishlistConfig.cookieName
    }=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  },

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  },

  addItem(productHandle) {
    const items = this.getItems();
    const handle = String(productHandle);

    if (items.includes(handle)) return false;
    if (items.length >= WishlistConfig.maxItems) {
      WishlistNotification.show("Lista życzeń jest pełna (max 200)");
      return false;
    }

    items.push(handle);
    this.setItems(items);
    return true;
  },

  removeItem(productHandle) {
    const items = this.getItems();
    const handle = String(productHandle);
    const index = items.indexOf(handle);

    if (index === -1) return false;

    items.splice(index, 1);
    this.setItems(items);
    return true;
  },

  hasItem(productHandle) {
    return this.getItems().includes(String(productHandle));
  },

  getCount() {
    return this.getItems().length;
  },

  clear() {
    this.setItems([]);
  },
};

const WishlistCache = {
  products: new Map(),
  renderedHTML: null,
  renderedHandles: null,

  getProduct(handle) {
    return this.products.get(handle) || null;
  },

  setProduct(handle, product) {
    this.products.set(handle, product);
    this.saveToStorage();
  },

  removeProduct(handle) {
    this.products.delete(handle);
    this.invalidateHTML();
    this.saveToStorage();
  },

  getHTML() {
    const currentHandles = WishlistStorage.getItems().join(",");
    if (this.renderedHTML && this.renderedHandles === currentHandles) {
      return this.renderedHTML;
    }
    return null;
  },

  setHTML(html) {
    this.renderedHTML = html;
    this.renderedHandles = WishlistStorage.getItems().join(",");
  },

  invalidateHTML() {
    this.renderedHTML = null;
    this.renderedHandles = null;
  },

  saveToStorage() {
    try {
      const data = {};
      this.products.forEach((product, handle) => {
        data[handle] = product;
      });
      sessionStorage.setItem("wishlist_products_cache", JSON.stringify(data));
    } catch (e) {
      console.warn("Wishlist: Could not save cache to sessionStorage", e);
    }
  },

  loadFromStorage() {
    try {
      const data = sessionStorage.getItem("wishlist_products_cache");
      if (data) {
        const parsed = JSON.parse(data);
        Object.keys(parsed).forEach((handle) => {
          this.products.set(handle, parsed[handle]);
        });
      }
    } catch (e) {
      console.warn("Wishlist: Could not load cache from sessionStorage", e);
    }
  },

  hasAllProducts(handles) {
    return handles.every((handle) => this.products.has(handle));
  },

  getProductsForHandles(handles) {
    const products = [];
    handles.forEach((handle) => {
      const product = this.products.get(handle);
      if (product) products.push(product);
    });
    return products;
  },
};

const WishlistNotification = {
  element: null,
  timeout: null,

  init() {
    if (document.getElementById("wishlist-notification")) return;

    const notification = document.createElement("div");
    notification.id = "wishlist-notification";
    notification.className = "wishlist-notification";
    notification.innerHTML = `
      <div class="wishlist-notification__content">
        <div class="wishlist-notification__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </div>
        <span class="wishlist-notification__text"></span>
      </div>
    `;
    document.body.appendChild(notification);
    this.element = notification;
  },

  show(message) {
    if (!this.element) this.init();

    clearTimeout(this.timeout);

    this.element.querySelector(".wishlist-notification__text").textContent =
      message;
    this.element.classList.add("active");

    this.timeout = setTimeout(() => {
      this.element.classList.remove("active");
    }, 3000);
  },
};

const WishlistAPI = {
  async fetchProduct(productHandle) {
    const cached = WishlistCache.getProduct(productHandle);
    if (cached) {
      this.revalidateProduct(productHandle);
      return cached;
    }

    return await this.fetchFreshProduct(productHandle);
  },

  async fetchFreshProduct(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.json`);
      if (!response.ok) throw new Error("Product not found");

      const data = await response.json();
      WishlistCache.setProduct(productHandle, data.product);
      return data.product;
    } catch (e) {
      console.error("Wishlist: Error fetching product", productHandle, e);
      return null;
    }
  },

  async revalidateProduct(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.json`);
      if (!response.ok) return;

      const data = await response.json();
      const oldProduct = WishlistCache.getProduct(productHandle);

      if (JSON.stringify(oldProduct) !== JSON.stringify(data.product)) {
        WishlistCache.setProduct(productHandle, data.product);
        WishlistCache.invalidateHTML();

        const drawer = document.querySelector("wishlist-drawer");
        if (drawer && drawer.isOpen) {
          drawer.renderItems();
        }
      }
    } catch (e) {
      console.warn("Wishlist: Revalidation failed", productHandle, e);
    }
  },

  async prefetchProducts(handles) {
    const uncached = handles.filter((h) => !WishlistCache.getProduct(h));
    await Promise.all(uncached.map((h) => this.fetchFreshProduct(h)));
  },
};

class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.backdrop = null;
    this.itemsContainer = null;
    this.isOpen = false;
    this.isReady = false;
  }

  connectedCallback() {
    this.backdrop = document.getElementById("wishlist-drawer-backdrop");
    this.itemsContainer = document.getElementById("wishlist-items");
    this.setupEventListeners();

    WishlistCache.loadFromStorage();

    this.classList.add("is-ready");
    this.isReady = true;
  }

  setupEventListeners() {
    this.addEventListener("keyup", (e) => {
      if (e.code === "Escape") this.close();
    });

    const closeBtn = this.querySelector(".wishlist-drawer__close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => this.close());
    }

    if (this.backdrop) {
      this.backdrop.addEventListener("click", () => this.close());
    }

    const overlay = this.querySelector(".wishlist-drawer__overlay");
    if (overlay) {
      overlay.addEventListener("click", () => this.close());
    }

    this.itemsContainer?.addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".wishlist-item__remove");
      if (removeBtn) {
        const item = removeBtn.closest(".wishlist-item");
        const productHandle = item?.dataset.productHandle;
        if (productHandle) this.removeItem(productHandle);
        return;
      }

      const productLink = e.target.closest(
        ".wishlist-action-btn--primary, .wishlist-item__image-link, .wishlist-item__name"
      );
      if (productLink) {
        this.forceClose();
      }
    });
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const header =
      document.querySelector("sticky-header") ||
      document.querySelector("header");
    if (header) {
      header.style.paddingRight = `${scrollbarWidth}px`;
    }

    if (this.backdrop) {
      this.backdrop.classList.add("is-visible");
    }

    this.render();

    requestAnimationFrame(() => {
      this.classList.add("active");
      document.body.classList.add("wishlist-drawer-open");
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.classList.remove("active");
    document.body.classList.remove("wishlist-drawer-open");

    if (this.backdrop) {
      this.backdrop.classList.remove("is-visible");
    }

    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      const header =
        document.querySelector("sticky-header") ||
        document.querySelector("header");
      if (header) {
        header.style.paddingRight = "";
      }
    }, 400);
  }

  forceClose() {
    this.isOpen = false;
    this.classList.remove("active");

    if (this.backdrop) {
      this.backdrop.classList.remove("is-visible");
    }

    document.body.classList.remove("wishlist-drawer-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    const header =
      document.querySelector("sticky-header") ||
      document.querySelector("header");
    if (header) {
      header.style.paddingRight = "";
    }
  }

  showLoading() {
    this.classList.remove("is-empty");
    this.classList.add("is-loading");
    if (this.itemsContainer) {
      this.itemsContainer.innerHTML =
        '<div class="wishlist-drawer__loading"><div class="wishlist-loading-spinner"></div></div>';
    }
  }

  hideLoading() {
    this.classList.remove("is-loading");
  }

  async render() {
    const handles = WishlistStorage.getItems();

    if (handles.length === 0) {
      this.classList.add("is-empty");
      this.classList.remove("is-loading");
      if (this.itemsContainer) this.itemsContainer.innerHTML = "";
      return;
    }

    this.classList.remove("is-empty");

    const cachedHTML = WishlistCache.getHTML();
    if (cachedHTML) {
      this.itemsContainer.innerHTML = cachedHTML;
      this.hideLoading();
      this.revalidateInBackground(handles);
      return;
    }

    if (WishlistCache.hasAllProducts(handles)) {
      this.renderItems();
      this.revalidateInBackground(handles);
      return;
    }

    this.showLoading();
    await WishlistAPI.prefetchProducts(handles);
    this.renderItems();
  }

  async revalidateInBackground(handles) {
    for (const handle of handles) {
      WishlistAPI.revalidateProduct(handle);
    }
  }

  renderItems() {
    const handles = WishlistStorage.getItems();

    if (handles.length === 0) {
      this.classList.add("is-empty");
      this.itemsContainer.innerHTML = "";
      return;
    }

    const itemsHTML = [];

    for (const handle of handles) {
      const product = WishlistCache.getProduct(handle);
      if (product) {
        itemsHTML.push(this.createItemHTML(product));
      }
    }

    const html = itemsHTML.join("");
    WishlistCache.setHTML(html);

    this.itemsContainer.innerHTML = html;
    this.hideLoading();
    WishlistUI.updateCount();
  }

  createItemHTML(product) {
    const title = product.title || "Produkt";
    const url = `/products/${product.handle}`;
    const image = product.images?.[0]?.src || "";
    const price = product.price_min || product.variants[0]?.price;
    const comparePrice =
      product.compare_at_price_max || product.variants[0]?.compare_at_price;
    const available = product.available;

    const fromLabel = window.theme?.strings?.from_price || "od";

    const priceHTML =
      comparePrice && comparePrice > price
        ? `<span class="wishlist-price-compare">${this.formatMoney(
            comparePrice
          )}</span>
           <span class="wishlist-price-current">${fromLabel} ${this.formatMoney(
            price
          )}</span>`
        : `<span class="wishlist-price-current">${fromLabel} ${this.formatMoney(
            price
          )}</span>`;

    const inStockLabel = window.theme?.strings?.in_stock || "W magazynie";
    const availabilityHTML = available
      ? `<span class="wishlist-item__availability in-stock">${inStockLabel}</span>`
      : "";

    const viewProductLabel =
      window.theme?.strings?.view_product || "ZOBACZ PRODUKT";
    const removeLabel =
      window.theme?.strings?.remove_from_wishlist || "Usuń z listy życzeń";

    return `
      <div class="wishlist-item" data-product-handle="${product.handle}">
        <div class="wishlist-item__image">
          <a href="${url}" class="wishlist-item__image-link">
            <img class="wishlist-item__img" src="${
              image ? image.replace(/(\.[^.]+)$/, "_300x300$1") : ""
            }" alt="${title}" loading="lazy" width="140" height="140">
          </a>
        </div>

        <div class="wishlist-item__content">
          <div class="wishlist-item__details">
            <a href="${url}" class="wishlist-item__name">${title}</a>
            <div class="wishlist-item__price">${priceHTML}</div>
            ${availabilityHTML}
          </div>

          <div class="wishlist-item__actions">
            <a href="${url}" class="wishlist-action-btn wishlist-action-btn--primary">
              <span class="wishlist-action-btn__text">${viewProductLabel}</span>
            </a>
            <button type="button" class="wishlist-item__remove wishlist-action-btn wishlist-action-btn--remove" aria-label="${removeLabel}">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 5H14M6 5V4C6 3.44772 6.44772 3 7 3H11C11.5523 3 12 3.44772 12 4V5M13 5V14C13 14.5523 12.5523 15 12 15H6C5.44772 15 5 14.5523 5 14V5H13Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  formatMoney(cents) {
    if (typeof cents === "string") {
      cents = parseFloat(cents.replace(".", ""));
    }

    const moneyFormat =
      window.theme?.moneyFormat ||
      window.Shopify?.money_format ||
      "{{amount}} PLN";
    const amount = cents / 100;

    let value = "";
    const formatString =
      moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || "amount";

    switch (formatString) {
      case "amount":
        value = amount.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
        break;
      case "amount_no_decimals":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
        break;
      case "amount_with_comma_separator":
        value = amount
          .toFixed(2)
          .replace(".", ",")
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
        break;
      case "amount_with_space_separator":
        value = amount
          .toFixed(2)
          .replace(".", ",")
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
        break;
      case "amount_no_decimals_with_space_separator":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
        break;
      default:
        value = amount.toFixed(2);
    }

    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  removeItem(productHandle) {
    WishlistStorage.removeItem(productHandle);
    WishlistCache.removeProduct(productHandle);

    const item = this.itemsContainer.querySelector(
      `[data-product-handle="${productHandle}"]`
    );
    if (item) {
      item.style.opacity = "0";
      item.style.transform = "translateX(20px)";
      item.style.transition = "opacity 0.2s ease, transform 0.2s ease";
      setTimeout(() => {
        item.remove();
        if (WishlistStorage.getCount() === 0) {
          this.classList.add("is-empty");
        }
      }, 200);
    }

    WishlistUI.updateCount();
    WishlistUI.updateButtons();
    WishlistNotification.show(
      window.theme?.strings?.removed_from_wishlist || "Usunięto z listy życzeń"
    );
  }
}

customElements.define("wishlist-drawer", WishlistDrawer);

const WishlistUI = {
  init() {
    this.setupWishlistIcon();
    this.updateCount();
    this.updateButtons();
    this.setupWishlistButtons();
    WishlistNotification.init();
    WishlistCache.loadFromStorage();

    this.prefetchWishlistProducts();
  },

  prefetchWishlistProducts() {
    const handles = WishlistStorage.getItems();
    if (handles.length > 0) {
      WishlistAPI.prefetchProducts(handles);
    }
  },

  setupWishlistIcon() {
    const wishlistIcon = document.querySelector("#wishlist-icon-bubble");
    if (!wishlistIcon) return;

    wishlistIcon.addEventListener("click", (e) => {
      e.preventDefault();
      const drawer = document.querySelector("wishlist-drawer");
      if (drawer) drawer.open();
    });
  },

  setupWishlistButtons() {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(
        ".brutalist-wishlist-btn, .product-card__wishlist-btn"
      );
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const productHandle = btn.dataset.productHandle;
      if (!productHandle) return;

      if (WishlistStorage.hasItem(productHandle)) {
        WishlistStorage.removeItem(productHandle);
        WishlistCache.removeProduct(productHandle);
        btn.classList.remove("is-active");
        btn.setAttribute("aria-pressed", "false");
        WishlistNotification.show(
          window.theme?.strings?.removed_from_wishlist ||
            "Usunięto z listy życzeń"
        );

        const drawer = document.querySelector("wishlist-drawer");
        if (drawer && drawer.isOpen) {
          drawer.renderItems();
        }
      } else {
        if (WishlistStorage.addItem(productHandle)) {
          btn.classList.add("is-active");
          btn.setAttribute("aria-pressed", "true");
          WishlistNotification.show(
            window.theme?.strings?.added_to_wishlist || "Dodano do listy życzeń"
          );
          WishlistAPI.fetchFreshProduct(productHandle).then(() => {
            WishlistCache.invalidateHTML();
            const drawer = document.querySelector("wishlist-drawer");
            if (drawer && drawer.isOpen) {
              drawer.renderItems();
            }
          });
        }
      }

      this.updateCount();
      this.updateButtons();
    });
  },

  updateCount() {
    const count = WishlistStorage.getCount();
    const bubbles = document.querySelectorAll(".wishlist-count-bubble");

    bubbles.forEach((bubble) => {
      if (count > 0) {
        bubble.textContent = count > 99 ? "99+" : count;
        bubble.style.display = "flex";
      } else {
        bubble.textContent = "";
        bubble.style.display = "none";
      }
    });
  },

  updateButtons() {
    const buttons = document.querySelectorAll(
      ".brutalist-wishlist-btn, .product-card__wishlist-btn"
    );

    buttons.forEach((btn) => {
      const productHandle = btn.dataset.productHandle;
      if (productHandle && WishlistStorage.hasItem(productHandle)) {
        btn.classList.add("is-active");
        btn.setAttribute("aria-pressed", "true");
      } else {
        btn.classList.remove("is-active");
        btn.setAttribute("aria-pressed", "false");
      }
    });
  },
};

document.addEventListener("DOMContentLoaded", () => {
  WishlistUI.init();
});

window.addEventListener("pageshow", (e) => {
  const drawer = document.querySelector("wishlist-drawer");
  if (drawer) {
    drawer.forceClose();
  }
  WishlistUI.updateCount();
  WishlistUI.updateButtons();
});

window.WishlistStorage = WishlistStorage;
window.WishlistUI = WishlistUI;
window.WishlistCache = WishlistCache;
window.WishlistAPI = WishlistAPI;
